// app/(routes)/careers/[slug]/components/career-detail-client.tsx
"use client";

import { useState } from "react";
import { Easing, motion } from "framer-motion";
import { CheckCircle, ChevronRight, Briefcase, ArrowRight } from "lucide-react";
import Link from "next/link";
import type { JobOpening } from "@prisma/client";
import type { FormConfig } from "@/app/_lib/types/careers";
import { Rail } from "@/app/_components/marketing/rail";
import ApplicationModal from "./application-modal";

const sectionVariants = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] as unknown as Easing[] },
  },
};

export default function CareerDetailClient({
  job,
  formConfig,
  isOpen,
}: {
  job: JobOpening;
  formConfig: FormConfig;
  isOpen: boolean;
}) {
  const [applyOpen, setApplyOpen] = useState(false);

  return (
    <>
      {/* ── Main content ── */}
      <Rail bottom innerClassName="py-10 pb-28 lg:pb-10">
      <div className="max-w-3xl w-full mx-auto space-y-6">
        {/* About Role */}
        <motion.div
          variants={sectionVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-30px" }}
          className="bg-white border border-border p-6"
        >
          <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
            About this Role
          </h2>
          <p className="text-[14px] text-gray-600 leading-relaxed whitespace-pre-line">
            {job.jobDescription}
          </p>
        </motion.div>

        {/* Responsibilities */}
        {job.keyResponsibilities?.length > 0 && (
          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-30px" }}
            className="bg-white border border-border p-6"
          >
            <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
              Key Responsibilities
            </h2>
            <ul className="space-y-3">
              {job.keyResponsibilities.map((r, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05, duration: 0.35 }}
                  className="flex items-start gap-2.5 text-[14px] text-gray-700 leading-relaxed"
                >
                  <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <span>{r}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}

        {/* Requirements */}
        {job.requirements?.length > 0 && (
          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-30px" }}
            className="bg-white border border-border p-6"
          >
            <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
              Requirements
            </h2>
            <ul className="space-y-3">
              {job.requirements.map((r, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05, duration: 0.35 }}
                  className="flex items-start gap-2.5 text-[14px] text-gray-700 leading-relaxed"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                  <span>{r}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}

        {/* Closed notice */}
        {!isOpen && (
          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="bg-red-50 border border-red-200 px-5 py-4 text-xs text-red-700 font-medium"
          >
            This position is currently closed. Please browse our other active
            open listings.
          </motion.div>
        )}

        {/* Desktop apply card */}
        {isOpen && (
          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="hidden lg:block bg-white border border-border p-6 space-y-4"
          >
            <div className="space-y-1">
              <h3 className="font-semibold text-lg">{job.title}</h3>
              <p className="text-xs text-muted-foreground">
                {job.location} ·{" "}
                {job.years === 0 ? "Entry Level" : `${job.years}+ yrs`}
              </p>
            </div>
            <button
              onClick={() => setApplyOpen(true)}
              className="w-full h-12 bg-primary text-white font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity active:scale-[0.98]"
            >
              Apply for this Role
              <ChevronRight className="w-4 h-4" />
            </button>
          </motion.div>
        )}

        {/* Closed desktop card */}
        {!isOpen && (
          <div className="hidden lg:flex border border-dashed border-border bg-white p-10 flex-col items-center text-center space-y-4">
            <div className="w-14 h-14 bg-accent flex items-center justify-center mx-auto">
              <Briefcase className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="font-semibold text-gray-700">Position Closed</p>
            <p className="text-xs text-muted-foreground max-w-xs mx-auto">
              We are no longer accepting applications for this role.
            </p>
            <Link
              href="/careers"
              className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline underline-offset-2"
            >
              See open positions <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}
      </div>
      </Rail>

      {/* ── Mobile sticky bottom bar ── */}
      {isOpen && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.4, ease: "easeOut" }}
          className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-t border-border px-5 py-4 flex items-center gap-3"
          style={{
            paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))",
          }}
        >
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">{job.title}</p>
            <p className="text-xs text-muted-foreground">{job.location}</p>
          </div>
          <button
            onClick={() => setApplyOpen(true)}
            className="shrink-0 h-11 px-6 bg-primary text-white font-semibold flex items-center gap-2 hover:opacity-90 transition-opacity active:scale-[0.98] text-sm"
          >
            Apply Now
            <ArrowRight className="w-4 h-4" />
          </button>
        </motion.div>
      )}

      {/* ── Application modal/sheet ── */}
      <ApplicationModal
        open={applyOpen}
        onClose={() => setApplyOpen(false)}
        job={job}
        formConfig={formConfig}
      />
    </>
  );
}
